/**
 * Occluded-window imagery pump (Task #270).
 *
 * Even with `requestRenderMode: false`, Cesium dispatches imagery/terrain tile
 * requests ONLY inside render frames, and render frames ride
 * requestAnimationFrame. Chrome stops rAF entirely for hidden windows — and a
 * fully OCCLUDED window counts as hidden (`document.visibilityState ===
 * 'hidden'`) — so a wall loaded while its window is covered never issues a
 * single ion request: the tile load queue sits non-empty while frameNumber
 * stays frozen (verified live on the GOC wall: zero api.cesium.com requests
 * for minutes, then a burst on reveal that reads as 100+ seconds of black
 * globe — the #270 stall).
 *
 * Fix = two cooperating wake paths:
 *
 *  1. HIDDEN PUMP — while the document is hidden AND the globe's tile load
 *     queue is non-empty, drive `viewer.render()` from a setTimeout chain so
 *     request dispatch + tile processing keep advancing without rAF. The chain
 *     is WORK-GATED: it stops the moment `globe.tilesLoaded` flips true, so an
 *     occluded-but-streamed wall costs ZERO renders for however long it sits
 *     covered, and each hide flip costs at most one queue-refresh frame.
 *  2. VISIBILITY BURST — on every hidden→visible flip, cancel the chain and
 *     force one immediate render so dispatch resumes NOW (not at the next
 *     natural rAF); the revived rAF loop owns rendering from then on.
 *
 * What Chrome's timer throttling allows — accepted, not evaded:
 *  - hidden ≤ ~5 min: chained timers clamp to ≥1 s → ~1 render/s; a cold wall
 *    view streams fully in the background within a minute or two.
 *  - hidden > ~5 min: "intensive throttling" coalesces timers to ~1/min → a
 *    still-loading wall trickles at ~1 render/min until revealed, and the
 *    visibility burst + resumed rAF complete the stream at reveal. We accept
 *    that ceiling rather than fighting it (no Worker-timer bypass, no silent
 *    audio): the pump's job is the common case — a wall occluded for
 *    seconds-to-minutes mid-multitask paints BEFORE eyes return to it.
 *
 * Why not the alternatives: a visibility burst alone leaves the whole ~30 s
 * cold stream to start exactly when the operator looks (the founder-observed
 * failure); an always-on timer render tick burns frames forever on every
 * loaded-but-hidden window (battery fire). Work-gated pump + burst does both
 * jobs with a hard cost bound.
 */

/** The slice of the Cesium Viewer API the pump touches — structural so it can
 *  be unit-tested with a lightweight fake (cesiumRenderGuard pattern). */
export interface OcclusionPumpViewer {
  isDestroyed(): boolean;
  render(): void;
  scene: { globe: { tilesLoaded: boolean } };
}

/** The slice of `document` the pump reads — injectable for tests. */
export interface OcclusionPumpDocument {
  visibilityState: string;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

export interface OcclusionPumpOptions {
  /** Requested delay between hidden-pump renders, in ms. Chrome clamps the
   *  real delay upward while hidden (≥1 s normally, ~60 s under intensive
   *  throttling) — we request and accept what we get. Default 1000. */
  pumpMs?: number;
  /** Injectable for tests. Default: the real `document`. */
  doc?: OcclusionPumpDocument;
  /** Timer pair. Injectable for tests. Default setTimeout/clearTimeout. */
  schedule?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  cancel?: (id: ReturnType<typeof setTimeout>) => void;
  /** Fault sink (a manual render throwing). Default `console.warn`. */
  log?: (message: string, ...rest: unknown[]) => void;
}

export interface OcclusionPumpHandle {
  /** Detach the visibility listener + cancel any pending pump tick. */
  destroy(): void;
  /** True while a hidden-pump tick is scheduled. */
  isPumping(): boolean;
  /** Live counters for QA — exposed as `window.__tsrOcclusionPump`. */
  stats(): { pumpRenders: number; kickRenders: number; faults: number };
}

/**
 * Install the pump on a viewer. Returns a handle MapView disposes on unmount
 * (before `viewer.destroy()`).
 */
export function installOcclusionImageryPump(
  viewer: OcclusionPumpViewer,
  options: OcclusionPumpOptions = {},
): OcclusionPumpHandle {
  const pumpMs = options.pumpMs ?? 1000;
  const doc = options.doc ?? document;
  const schedule = options.schedule ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const cancel = options.cancel ?? ((id: ReturnType<typeof setTimeout>) => clearTimeout(id));
  const log = options.log ?? ((msg: string, ...rest: unknown[]) => console.warn(msg, ...rest));

  let timer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;
  let pumpRenders = 0;
  let kickRenders = 0;
  let faults = 0;

  const renderSafely = (context: string): boolean => {
    try {
      viewer.render();
      return true;
    } catch (error) {
      faults += 1;
      log(
        `[cesiumOcclusionPump] manual render threw during ${context} — pump parked until the next visibility change`,
        error,
      );
      return false;
    }
  };

  const stopPump = (): void => {
    if (timer !== null) {
      cancel(timer);
      timer = null;
    }
  };

  const tick = (): void => {
    timer = null;
    if (destroyed || viewer.isDestroyed()) return;
    // Visibility can race the chain (the event may not have fired yet): once
    // the window is visible again rAF owns rendering — stand down.
    if (doc.visibilityState !== 'hidden') return;
    // A deterministic render fault would otherwise log at pump rate — park the
    // chain instead; the next visibility flip re-arms it.
    if (!renderSafely('hidden pump')) return;
    pumpRenders += 1;
    // Work gate: a drained queue means nothing left to dispatch — stop, so an
    // occluded-but-loaded wall costs zero renders. The render above refreshed
    // the queue state, so a layer/terrain change that re-queued work while
    // hidden reads as tilesLoaded=false here and keeps the chain alive.
    if (viewer.scene.globe.tilesLoaded) return;
    timer = schedule(tick, pumpMs);
  };

  const startPump = (): void => {
    if (destroyed || timer !== null) return;
    // First tick at 0 ms so dispatch begins immediately on hide/install (a 0 ms
    // timeout still fires promptly in a hidden tab at low nesting depth); the
    // chain then settles to pumpMs and Chrome's clamps take over.
    timer = schedule(tick, 0);
  };

  const onVisibilityChange = (): void => {
    if (destroyed) return;
    if (doc.visibilityState === 'hidden') {
      startPump();
      return;
    }
    // Hidden→visible burst: resume dispatch NOW with one frame, then the
    // revived rAF loop owns rendering. Unconditional — it also rescues a chain
    // parked a minute out by intensive throttling.
    stopPump();
    if (!viewer.isDestroyed() && renderSafely('visibility burst')) kickRenders += 1;
  };

  doc.addEventListener('visibilitychange', onVisibilityChange);
  // The #270 case is a wall LOADED while already occluded — no visibilitychange
  // ever fires for it, so arm the pump straight from install.
  if (doc.visibilityState === 'hidden') startPump();

  return {
    destroy(): void {
      destroyed = true;
      stopPump();
      doc.removeEventListener('visibilitychange', onVisibilityChange);
    },
    isPumping: () => timer !== null,
    stats: () => ({ pumpRenders, kickRenders, faults }),
  };
}
